import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getAuthHeaders = () => {
  const token = localStorage.getItem('adminToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const NewsAgent = () => {
  const [queries, setQueries] = useState([]);
  const [settings, setSettings] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [notification, setNotification] = useState(null);
  const [activeTab, setActiveTab] = useState('queries'); // queries, settings, logs
  const [showQueryModal, setShowQueryModal] = useState(false);
  const [editingQuery, setEditingQuery] = useState(null);
  const [queryForm, setQueryForm] = useState({
    query_ru: '',
    query_en: '',
    category: 'customs',
    priority: 1,
    is_active: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [queriesRes, settingsRes, logsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/news-agent/queries`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/admin/news-agent/settings`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/admin/news-agent/logs?limit=20`, { headers: getAuthHeaders() })
      ]);
      
      setQueries(queriesRes.data);
      setSettings(settingsRes.data);
      setLogs(logsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      showNotification('Ошибка загрузки данных', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const runAgent = async () => {
    if (!window.confirm('Запустить AI-агент для генерации статей? Это может занять несколько минут.')) {
      return;
    }
    
    setRunning(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/news-agent/run`, 
        {}, 
        { headers: getAuthHeaders() }
      );
      
      showNotification(
        `Агент выполнен! Создано статей: ${response.data.articles_generated}, запросов: ${response.data.queries_processed}`
      );
      loadData();
    } catch (error) {
      console.error('Error running agent:', error);
      showNotification('Ошибка запуска агента: ' + (error.response?.data?.detail || error.message), 'error');
    } finally {
      setRunning(false);
    }
  };

  const toggleQueryActive = async (queryId, currentStatus) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/news-agent/queries/${queryId}`,
        { is_active: !currentStatus },
        { headers: getAuthHeaders() }
      );
      showNotification('Статус запроса обновлен');
      loadData();
    } catch (error) {
      showNotification('Ошибка обновления запроса', 'error');
    }
  };

  const deleteQuery = async (queryId) => {
    if (!window.confirm('Удалить этот поисковый запрос?')) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/admin/news-agent/queries/${queryId}`,
        { headers: getAuthHeaders() }
      );
      showNotification('Запрос удален');
      loadData();
    } catch (error) {
      showNotification('Ошибка удаления запроса', 'error');
    }
  };

  const openQueryModal = (query = null) => {
    if (query) {
      setEditingQuery(query);
      setQueryForm({
        query_ru: query.query_ru,
        query_en: query.query_en,
        category: query.category,
        priority: query.priority,
        is_active: query.is_active
      });
    } else {
      setEditingQuery(null);
      setQueryForm({
        query_ru: '',
        query_en: '',
        category: 'customs',
        priority: 1,
        is_active: true
      });
    }
    setShowQueryModal(true);
  };

  const closeQueryModal = () => {
    setShowQueryModal(false);
    setEditingQuery(null);
  };

  const handleQuerySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingQuery) {
        await axios.put(
          `${API_URL}/api/admin/news-agent/queries/${editingQuery.id}`,
          queryForm,
          { headers: getAuthHeaders() }
        );
        showNotification('Запрос обновлен');
      } else {
        await axios.post(
          `${API_URL}/api/admin/news-agent/queries`,
          queryForm,
          { headers: getAuthHeaders() }
        );
        showNotification('Запрос создан');
      }
      closeQueryModal();
      loadData();
    } catch (error) {
      showNotification('Ошибка сохранения запроса', 'error');
    }
  };

  const updateSettings = async () => {
    try {
      await axios.put(
        `${API_URL}/api/admin/news-agent/settings`,
        settings,
        { headers: getAuthHeaders() }
      );
      showNotification('Настройки сохранены');
    } catch (error) {
      showNotification('Ошибка сохранения настроек', 'error');
    }
  };

  if (loading) {
    return <div className="text-center py-12">Загрузка...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">AI News Agent</h1>
          <p className="text-gray-600 mt-2">Автоматическая генерация статей из новостей</p>
        </div>
        <button
          onClick={runAgent}
          disabled={running}
          className="bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg font-medium transition flex items-center"
        >
          {running ? (
            <>
              <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
              </svg>
              Генерация...
            </>
          ) : (
            '▶ Запустить агент'
          )}
        </button>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`p-4 rounded-lg ${
          notification.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {notification.message}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex space-x-8">
          <button
            onClick={() => setActiveTab('queries')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'queries'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Поисковые запросы ({queries.length})
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'settings'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Настройки
          </button>
          <button
            onClick={() => setActiveTab('logs')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'logs'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            История ({logs.length})
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'queries' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => openQueryModal()}
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg font-medium transition"
            >
              + Добавить запрос
            </button>
          </div>
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Запрос (RU)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Категория</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Приоритет</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Статус</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Действия</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {queries.map((query) => (
                <tr key={query.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900">{query.query_ru}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{query.category}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{query.priority}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => toggleQueryActive(query.id, query.is_active)}
                      className={`px-2 py-1 text-xs rounded-full ${
                        query.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {query.is_active ? 'Активен' : 'Неактивен'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm space-x-3">
                    <button
                      onClick={() => openQueryModal(query)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Редактировать
                    </button>
                    <button
                      onClick={() => deleteQuery(query.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Удалить
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </div>
      )}

      {activeTab === 'settings' && settings && (
        <div className="bg-white rounded-lg shadow-sm p-6 max-w-2xl">
          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={settings.auto_generate}
                onChange={(e) => setSettings({...settings, auto_generate: e.target.checked})}
                className="h-4 w-4 text-orange-600"
              />
              <label className="ml-2 text-sm text-gray-900">Автоматическая генерация статей</label>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Частота генерации</label>
              <select
                value={settings.generation_frequency}
                onChange={(e) => setSettings({...settings, generation_frequency: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg"
              >
                <option value="daily">Ежедневно</option>
                <option value="weekly">Еженедельно</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Макс. статей за раз</label>
              <input
                type="number"
                value={settings.max_articles_per_run}
                onChange={(e) => setSettings({...settings, max_articles_per_run: parseInt(e.target.value)})}
                min="1"
                max="10"
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Размер контекста поиска</label>
              <select
                value={settings.search_context_size}
                onChange={(e) => setSettings({...settings, search_context_size: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg"
              >
                <option value="low">Низкий (быстро)</option>
                <option value="medium">Средний</option>
                <option value="high">Высокий (качество)</option>
              </select>
            </div>
            
            <button
              onClick={updateSettings}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg font-medium transition"
            >
              Сохранить настройки
            </button>
          </div>
        </div>
      )}

      {activeTab === 'logs' && (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Дата</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Запросов</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Статей</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Статус</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {new Date(log.created_at).toLocaleString('ru-RU')}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">{log.query_used}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{log.articles_generated}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      log.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {log.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Query Modal */}
      {showQueryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingQuery ? 'Редактировать запрос' : 'Создать запрос'}
              </h2>
            </div>

            <form onSubmit={handleQuerySubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Запрос (RU) *</label>
                  <input
                    type="text"
                    value={queryForm.query_ru}
                    onChange={(e) => setQueryForm({...queryForm, query_ru: e.target.value})}
                    required
                    placeholder="новости таможня Россия 2025"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Запрос (EN) *</label>
                  <input
                    type="text"
                    value={queryForm.query_en}
                    onChange={(e) => setQueryForm({...queryForm, query_en: e.target.value})}
                    required
                    placeholder="Russian customs news 2025"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Категория *</label>
                  <select
                    value={queryForm.category}
                    onChange={(e) => setQueryForm({...queryForm, category: e.target.value})}
                    required
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="customs">Таможня</option>
                    <option value="radiofrequency">Радиочастоты</option>
                    <option value="import_export">Импорт/Экспорт</option>
                    <option value="marking">Маркировка</option>
                    <option value="certification">Сертификация</option>
                    <option value="general">Общие</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Приоритет</label>
                  <input
                    type="number"
                    value={queryForm.priority}
                    onChange={(e) => setQueryForm({...queryForm, priority: parseInt(e.target.value)})}
                    min="1"
                    max="10"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Чем выше приоритет, тем чаще используется запрос</p>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={queryForm.is_active}
                  onChange={(e) => setQueryForm({...queryForm, is_active: e.target.checked})}
                  className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">Активен</label>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={closeQueryModal}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg"
                >
                  {editingQuery ? 'Сохранить' : 'Создать'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsAgent;
